import React from "react";
import Image from "next/image";

const cmaRoles = [
  "Financial Analyst",
  "Cost Accountant",
  "Budget Analyst",
  "Finance Manager",
  "Controller",
  "CFO",
];

/* Logos render 22px tall on desktop, 16px on mobile, so ImageKit is asked for
   a 3x (66px) WebP render — but never for more pixels than the source has.
   These PNGs are small originals (logo_3 is 188x24), and h-66 made ImageKit
   interpolate that 24px source up to 66px; the browser then downscaled the
   soft result, which is what looked pixelated. Capping the request at the
   native height serves the real resolution instead. `width`/`height` below
   are the displayed box, keeping each original's aspect ratio so nothing
   shifts or squashes.

   The <Image>s below are `unoptimized` on purpose: ImageKit has already sized
   and encoded these (WebP, q-90), and running them through the Next optimizer
   on top re-encodes at its default q-75 and resizes toward the nearest
   imageSizes bucket — 188px-wide art requested at 256px. Both steps chew up
   the thin strokes in a 16-22px logo. Serving ImageKit's render straight
   through keeps the edges clean. */
const LOGO_HEIGHT = 22;

/* Desktop height is capped at half the source height. These masters are tiny
   (logo_3 is 24px tall, logo_1/8/9 are 30px), and painting a 30px source into
   a 22px box means a 0.73x resample of hairline strokes on a 1x screen and a
   1.5x *stretch* on a retina one — both read as rough edges. Half the native
   height lands on 1:1 device pixels at 2x and a clean 2:1 downscale at 1x,
   which is the sharpest these files can render. Logos with enough pixels are
   unaffected and still sit at the full 22px. */
const desktopLogoHeight = (nativeHeight: number) =>
  Math.min(LOGO_HEIGHT, nativeHeight / 2);

/* Mobile scales each logo toward equal *area* instead of equal height. At one
   shared height a 7.8:1 wordmark (EisnerAmper) runs 125px while a 2.4:1 mark
   (KPMG) runs 39px, so the row reads as a few giants among midgets — and
   capping the width instead just squashed the wide ones to ~9px tall. Scaling
   by sqrt(reference / aspect) evens out how much ink each one puts on screen;
   the clamp stops the squarest marks from towering over the rest. */
const MOBILE_LOGO_HEIGHT = 16;
const REFERENCE_ASPECT = 4.5;
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const recruiterLogos = [
  { file: "logo_1", width: 180, height: 30 },
  { file: "logo_2", width: 262, height: 60 },
  { file: "logo_3", width: 188, height: 24 },
  { file: "logo_4", width: 318, height: 60 },
  { file: "logo_5", width: 141, height: 46 },
  { file: "logo_6", width: 150, height: 62 },
  { file: "logo_7", width: 214, height: 46 },
  { file: "logo_8", width: 160, height: 30 },
  { file: "logo_9", width: 152, height: 30 },
  { file: "logo_10", width: 212, height: 62 },
  { file: "logo_11", width: 166, height: 62 },
].map((logo) => ({
  file: logo.file,
  width: Math.round(
    (logo.width * desktopLogoHeight(logo.height)) / logo.height,
  ),
  height: desktopLogoHeight(logo.height),
  desktopHeight: desktopLogoHeight(logo.height),
  mobileHeight:
    Math.round(
      MOBILE_LOGO_HEIGHT *
        clamp(
          Math.sqrt(REFERENCE_ASPECT / (logo.width / logo.height)),
          0.75,
          1.2,
        ) *
        10,
    ) / 10,
  src: `https://ik.imagekit.io/mileseducation/miles_website/seo-pages/cma-course/${
    logo.file
  }.png?tr=h-${Math.min(logo.height, LOGO_HEIGHT * 3)},f-webp,q-90`,
}));

const CmaCourseCareer = () => {
  return (
    <section className="w-full bg-white md:pt-[50px] max-md:bg-[#F3F3F3] max-md:py-4">
      <div className="max-md:bg-white max-md:rounded-2xl max-md:mx-4 max-md:px-4 max-md:py-4 max-md:overflow-hidden">
        <div className="main_container max-md:px-0">
          {/* Section Heading */}
          <h2 className="md:text-[32px] text-[24px] font-bold text-left md:text-center leading-[1.2] md:mb-8 mb-6">
            <span className="text-[#39F]">Career Outlook</span>
            <span className="text-[#333]"> After CMA</span>
          </h2>

          <p className="text-[#333] md:text-sm text-xs font-normal leading-normal text-left md:text-center max-w-[900px] mx-auto">
            CMAs are in high demand across MNCs, Big 4 firms, and Fortune 500
            companies. As more of these organizations lean on AI for reporting
            and forecasting, they increasingly need CMAs who can interpret and
            act on AI-driven insights, not just produce them. Common roles after
            the CMA course include:
          </p>

          {/* Roles */}
          <div className="grid grid-cols-1 md:grid-cols-6 md:gap-[8px] gap-2.5 md:mt-6 mt-4">
            {cmaRoles?.map((role) => (
              <div
                key={role}
                className="bg-[#F5F5F5] border-l-4 border-[#39F] md:px-4 md:py-4 px-4 py-3"
              >
                <p className="text-[#333] md:text-sm text-xs font-normal leading-[1.4]">
                  {role}
                </p>
              </div>
            ))}
          </div>

          <p className="text-[#333] md:text-sm text-xs font-normal leading-normal text-left md:text-center max-w-[900px] mx-auto md:mt-6 mt-4">
            The average starting salary for a CMA in India ranges between ₹6–10
            LPA, and IMA&apos;s own 2023 Global Salary Survey shows CMAs earn
            21% more on average than non-certified peers.
          </p>

          {/* CTA */}
          <div className="flex justify-center md:mt-6 mt-4">
            <a
              href="https://www.mileseducation.com/cma/cma-salary"
              className="inline-flex items-center justify-center gap-2 h-[38px] px-5 py-3 rounded-tl-[20px] rounded-tr-[20px] rounded-br-[20px] rounded-bl-none bg-[#39F] text-white text-[13px] md:text-base font-bold leading-none whitespace-nowrap"
            >
              See detailed CMA salary data <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        {/* Recruiter logos — static row on desktop, marquee on mobile */}
        <div className="w-full bg-black overflow-hidden md:mt-8 mt-4 md:py-4 py-3 max-md:-mx-4 max-md:w-auto">
          <div className="main_container hidden md:flex items-center justify-between gap-6">
            {recruiterLogos?.map((logo) => (
              <Image
                key={logo.file}
                src={logo.src}
                alt=""
                width={logo.width}
                height={logo.height}
                className="w-auto object-contain"
                style={{ height: `${logo.desktopHeight}px` }}
                loading="lazy"
                unoptimized
              />
            ))}
          </div>

          <div className="md:hidden flex w-max items-center gap-5 animate-marquee-backward">
            {[...recruiterLogos, ...recruiterLogos]?.map((logo, idx) => (
              <Image
                key={`${logo.file}-${idx}`}
                src={logo.src}
                alt=""
                width={logo.width}
                height={logo.height}
                className="w-auto object-contain"
                style={{ height: `${logo.mobileHeight}px` }}
                loading="lazy"
                unoptimized
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CmaCourseCareer;
